import { IndexedTsPoint, TadpoleChart } from '@toggle/design-system';
import { getDirection, Indicator, indicatorsThresholds } from '@toggle/helpers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { useIndicatorStore } from '~/stores/use-indicator/useIndicator';
import { widgetSearchParamKeys } from '~/views/widgets/widget-utils';

import { IndicatorWrapper } from './indicator-wrapper/IndicatorWrapper';
import * as S from './IndicatorWidget.styles';

const getActiveIndicator = (searchParams: URLSearchParams) =>
  (searchParams.get(widgetSearchParamKeys.indicator) as Indicator) ??
  Indicator.TLI;

export const IndicatorWidget = () => {
  const { t } = useTranslation('indicator');
  const [searchParams] = useSearchParams();
  const [activeIndicator] = useState<Indicator>(() =>
    getActiveIndicator(searchParams)
  );

  const {
    initializeData,
    indicatorValues: { tsData },
    error,
  } = useIndicatorStore(state => ({
    initializeData: state.initialize,
    indicatorValues: state.indicators,
    error: state.error,
  }));

  useEffect(() => {
    if (!tsData) {
      initializeData(activeIndicator);
    }
  }, []);

  const legendElements = [
    { label: t('indicator:bearishBias'), component: <S.BearishBias /> },
    { label: t('indicator:bearishThreshold'), component: <S.BearishMarker /> },
    { label: t('indicator:bullishThreshold'), component: <S.BullishMarker /> },
    { label: t('indicator:bullishBias'), component: <S.BullishBias /> },
  ];

  const lastElements = (timeSeriesData: IndexedTsPoint[]): IndexedTsPoint[] => {
    return (
      timeSeriesData &&
      timeSeriesData.slice(timeSeriesData.length - 5, timeSeriesData.length)
    );
  };

  if (error) {
    return (
      <S.IndicatorWidgetWrapper>
        <ErrorMessageWrapper onClick={initializeData} />
      </S.IndicatorWidgetWrapper>
    );
  }

  return (
    <S.IndicatorWidgetWrapper>
      <S.HeadingContainer data-testid="widget-indicator-header">
        <S.Heading>{t('indicator:toggleIndicators')}</S.Heading>
        <S.HeaderTimePeriod> {t('indicator:timePeriod')}</S.HeaderTimePeriod>
      </S.HeadingContainer>
      <S.Container>
        {Object.entries(indicatorsThresholds).map(([key, value]) => {
          const { up, down, variants, mid } = value;
          const indicator = key as Indicator;
          const timeSeriesData = tsData?.[indicator];
          if (!timeSeriesData) {
            return undefined;
          }

          const lastData = timeSeriesData[timeSeriesData.length - 1];
          const direction = getDirection({
            indicator: indicator,
            up,
            down,
            value: lastData.value,
          });

          return (
            <IndicatorWrapper
              key={key}
              data-testid={`widget-indicator-${key}`}
              indicator={key}
              headerElement={
                <S.TitleWrapper variant={direction}>
                  {
                    /* i18next-extract-mark-context-next-line ["TLI", "RFI", "PI", "TI"] */
                    t('indicator:title.text', {
                      context: indicator,
                    })
                  }
                </S.TitleWrapper>
              }
              contentElement={
                <S.TitleWrapper>
                  <TadpoleChart
                    key={`widget-${key}`}
                    tsData={lastElements(timeSeriesData)}
                    upperBound={up}
                    lowerBound={down}
                    thresholdVariants={variants}
                    midValue={mid}
                    indicator={indicator}
                  />
                </S.TitleWrapper>
              }
            />
          );
        })}
      </S.Container>
      <S.Legend>
        {legendElements.map(({ label, component }) => (
          <S.LegendItem key={label}>
            {component}
            <S.Label>{label}</S.Label>
          </S.LegendItem>
        ))}
      </S.Legend>
    </S.IndicatorWidgetWrapper>
  );
};
